/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';

import { MAXIMUM_ITEM_DESCRIPTION_LENGTH } from '../../../utils/constants';
import { isNegativeAmount } from '../../../utils/ej-utils';
import { currencyFormatter, formatPercentage, getPromoText } from '../../../utils/formatting';
import DiscountFormatter from '../DiscountFormatter';

import { ConsumerDetails } from './ConsumerDetails';
import GreeceTaxExemptInfo from './GreeceTaxExemptInfo';

const { match, Default: _ } = require('@nike/rcf-fp');
const lodash = require('lodash');

const calculateTotal = (totals) => (totals.find((total) => total.TotalType === 'TransactionNetAmount')?.Amount);

const calculateSubtotal = (totals) => (totals.find((total) => total.TotalType === 'TransactionSubtotal')?.Amount);

const taxString = (item) => match(item)(
  [() => (['AUS', 'SGP', 'MYS', 'NZL'].includes(item.country)), 'GST Included In Total'],
  [() => item.country === 'THA', 'VAT Included in Total'],
  [_, item.RL_Tax + (item.country !== 'USA' && item.Percent ? ` ${formatPercentage(item.Percent)}%` : '')]
);

const TransactionTotals = ({
  purchasedItems, returnedItems, customerDetails, contactDetails, taxes, totals, country
}) => {
  const {
    locale: { storeCurrency, storeLocale }, RL_EmpNumber, RL_NumberItemReturned, RL_NumberItemSold, RL_Tax, RL_Total, RL_Subtotal, RL_YouSaved, RL_Name
  } = useSelector((state) => state);
  const total = calculateTotal(totals);
  const subtotal = calculateSubtotal(totals);

  const taxExemptionInfo = taxes?.find((tax) => tax.Tax?.TaxExemption)?.Tax?.TaxExemption;
  const certificateHolderName = taxExemptionInfo?.CertificateHolderName;

  const displayAmount = (number, isDiscount) => (isDiscount
    ? (
      <DiscountFormatter isNegative storeLocale={storeLocale}>
        {currencyFormatter(storeLocale, storeCurrency, number)}
      </DiscountFormatter>
    )
    : currencyFormatter(storeLocale, storeCurrency, number));

  //* Calculating the total saving (may need to replace it with the static value)
  const calculatePromoDiscount = (item) => {
    const discounts = item.RetailPriceModifiers.filter((obj) => obj.MethodCode !== 'PriceOverride' && obj?.Amount?.Action === 'Subtract');
    const subTotal = discounts.reduce((acc, obj) => acc + obj.Amount.Amount, 0);
    return subTotal;
  };
  const filterTran = purchasedItems.filter((obj) => obj.RetailPriceModifiers);
  const filterTranSaveAmount = purchasedItems.filter((obj) => !obj.voidFlag && obj.RetailPriceModifiers);
  const isSalesReceipt = !!purchasedItems.length;
  const discounts = filterTran.map((modifier) => modifier.RetailPriceModifiers).flat().filter((transaction) => transaction.IsTransactionLevel);
  const discountMessage = (message) => {
    const firstDiscount = lodash.first(message);
    const descriptions = getPromoText(firstDiscount).slice(0, MAXIMUM_ITEM_DESCRIPTION_LENGTH);
    const discountPercentage = !firstDiscount.PromotionID && !firstDiscount.Reason ? firstDiscount.Percent.Percent : '';
    return discountPercentage ? `${descriptions} ${formatPercentage(discountPercentage)}%` : descriptions;
  };
  const totalDiscount = (discount, discountId) => lodash(discount)
    .groupBy(discountId)
    .map((item) => ({
      message: discountMessage(item),
      reason: item[0]?.Employee?.ID ? `${RL_EmpNumber}: ${item[0]?.Employee?.ID}` : item[0].Reason?.Description,
      total: lodash.sumBy(item, 'Amount.Amount')
    }))
    .values();
  const discountSummary = [...totalDiscount(discounts.filter((pro) => pro.PromotionID), 'PromotionID'),
    ...totalDiscount(discounts.filter((pro) => !pro.PromotionID), 'Reason.Code')];
  const savingAmount = filterTranSaveAmount.reduce((sum, item) => sum + calculatePromoDiscount(item), 0) || null;
  const summedTaxesIndia = () => {
    const processItems = (items, refund) => items.reduce((acc, item) => {
      const { Taxes } = item;
      const amount = Taxes[0]?.TaxableAmount?.Amount || 0;

      return Taxes.reduce((newAcc, tax, index) => {
        const {
          HSNNumber,
          Percent,
          Rounding,
          TaxType
        } = tax;
        const key = `${HSNNumber}-${Percent}`;
        const existing = newAcc[key] || {
          CGST: 0,
          HSNNumber,
          IGST: 0,
          Refund: refund,
          SGST: 0,
          TaxableAmount: 0,
          TaxAmount: 0,
          TaxPercent: 0
        };

        const updated = {
          ...existing,
          TaxableAmount: index === 0 ? parseFloat((existing.TaxableAmount + amount)) : existing.TaxableAmount,
          TaxAmount: parseFloat((existing.TaxAmount + Rounding.Amount)),
          TaxPercent: Percent * (TaxType === 'IGST' ? 1 : 2),
          [TaxType]: parseFloat(((existing[TaxType] || 0) + Rounding.Amount)),
          ...(TaxType === 'CGST' && Percent !== 0 && { CGSTPercent: Percent }),
          ...(TaxType === 'SGST' && Percent !== 0 && { SGSTPercent: Percent }),
          ...(TaxType === 'IGST' && Percent !== 0 && { IGSTPercent: Percent })
        };

        return {
          ...newAcc,
          [key]: updated
        };
      }, acc);
    }, {});

    if (purchasedItems.length > 0) {
      return processItems(purchasedItems, false);
    } else if (returnedItems.length > 0) {
      return processItems(returnedItems.map((item) => item.Return), true);
    } else {
      return {};
    }
  };

  return (
    <Fragment key="TransactionTotals">
      {/* Change in 'Tax Exempt No.' related to src/components/reusable/ej-view-sections/TaxExemptTitle.js */}
      {(storeLocale === 'el-GR') && <GreeceTaxExemptInfo customers={contactDetails} totals={totals} />}
      {(storeLocale !== 'el-GR' && contactDetails?.[0]?.TaxCertificates?.[0]?.Number) && (
        <>
          <p>*** Consumer Details ***</p>
          <br />
          {certificateHolderName && (
            <article className="justify-between">
              <p>{RL_Name}: {certificateHolderName}</p>
            </article>
          )}
          <article className="justify-between">
            <p>Tax Exempt No.: {contactDetails?.[0]?.TaxCertificates?.[0]?.Number}</p><p />
          </article>
        </>
        ) }
      {subtotal && (
        <article className="justify-between">
          <p>{RL_Subtotal}</p>
          <p>{displayAmount(Math.abs(subtotal), isNegativeAmount(subtotal))}</p>
        </article>
      )}
      {['USA', 'TUR', 'ISR'].includes(country) && discountSummary.map((discountInfo, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <section key={i}>
          <article className="justify-between">
            <p className="pl2-sm">{discountInfo.message}</p>
            <p className="pl2-sm">{displayAmount(discountInfo.total, true)}</p>
          </article>
          {['TUR', 'ISR'].includes(country) && (
          <article className="justify-between">
            <p className="pl2-sm">{discountInfo.reason}</p>
          </article>
          )}
        </section>
      ))}
      {taxes.map((taxInfo) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={`taxInfo${taxInfo.SequenceNumber}`}>
          {country === 'KOR' && (
          <article key={`subtotal${taxInfo.SequenceNumber}`} className="justify-between">
            <p>{RL_Subtotal}</p>
            <p>{displayAmount(taxInfo.Tax?.TaxableAmount?.Amount, taxInfo.Tax?.TypeCode === 'Refund')}</p>
          </article>
          )}
          {!['CHN', 'MAC', 'HKG', 'TWN', 'FRA', 'IND'].includes(country) && (
            <article key={`tax${taxInfo.SequenceNumber}`} className="justify-between">
              <p>{taxString({ country, Percent: taxInfo.Tax?.Percent, RL_Tax })}</p>
              <p>{displayAmount(taxInfo.Tax?.Rounding?.Amount || taxInfo.Tax?.Amount?.Amount, taxInfo.Tax?.TypeCode === 'Refund')}</p>
            </article>
          )}
        </Fragment>
      ))}
      {country === 'IND' && (
      <>
        <p>*****Tax Details*****</p>
        {Object.entries(summedTaxesIndia()).map(([key, value]) => (
          <Fragment key={key}>
            <article className="justify-between">
              <p>Taxable value</p>
              <p>HSN: {value.HSNNumber}</p>
              <p>{displayAmount(value.TaxableAmount, value.Refund)}</p>
            </article>
            <article className="justify-between">
              <p className="pl2-sm">{RL_Tax} = {formatPercentage(value.TaxPercent)}%</p>
              <p>{displayAmount(value.TaxAmount, value.Refund)}</p>
            </article>
            {(!!value.CGST && value.SGST) && (
            <>
              <article className="justify-between">
                <p className="pl4-sm">CGST = {formatPercentage(value.CGSTPercent)}%</p>
                <p>{displayAmount(value.CGST, value.Refund)}</p>
              </article>
              <article className="justify-between">
                <p className="pl4-sm">SGST = {formatPercentage(value.SGSTPercent)}%</p>
                <p>{displayAmount(value.SGST, value.Refund)}</p>
              </article>
            </>
            )}
            {!!value.IGST && (
            <>
              <article className="justify-between">
                <p className="pl4-sm">IGST = {formatPercentage(value.IGSTPercent)}%</p>
                <p>{displayAmount(value.IGST, value.Refund)}</p>
              </article>
            </>
            )}
          </Fragment>
            ))}
      </>
      )}
      {country === 'FRA' && (
        <>
          <article className="justify-between">
            <p>----------------------------------------------------------</p>
          </article>
          {taxes.map((taxInfo) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`taxInfo${taxInfo.SequenceNumber}`}>
              <>
                <article className="justify-between">
                  <p className="pl2-sm">Total Excluding VAT: {displayAmount(taxInfo.Tax?.TaxableAmount?.Amount)}</p>
                </article>
                <article key={`tax${taxInfo.SequenceNumber}`} className="justify-between">
                  <p className="pl2-sm">{`${formatPercentage(taxInfo.Tax?.Percent)}%`}</p>
                  <p>{displayAmount(taxInfo.Tax?.Rounding?.Amount || taxInfo.Tax?.Amount?.Amount, taxInfo.Tax?.TypeCode === 'Refund')}</p>
                </article>
              </>
            </Fragment>
          ))}
          <article className="justify-between">
            <p>----------------------------------------------------------</p>
          </article>
        </>
      )}
      {country === 'THA'
        ? taxes.map((taxInfo, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <section key={i}>
            <article className="justify-between">
              <p>{'Total Excl of VAT'}{taxInfo.Tax?.Percent ? ` ${formatPercentage(taxInfo.Tax?.Percent)}%` : ''}</p>
              <p>{displayAmount(taxInfo.Tax?.TaxableAmount?.Amount, taxInfo.Tax?.TypeCode === 'Refund')}</p>
            </article>
            <article className="justify-between">
              <p>{'Total Incl of VAT'}{taxInfo.Tax?.Percent ? ` ${formatPercentage(taxInfo.Tax?.Percent)}%` : ''}</p>
              <p>{displayAmount(Math.abs((total)))}</p>
            </article>
          </section>
        ))
        : (
          <article className="justify-between">
            <p>{RL_Total}</p>
            <p>{displayAmount(Math.abs(total), Math.sign(total) === -1)}</p>
          </article>
        ) }
      {country === 'IND' && (
      <>
        {isSalesReceipt && (
          <article className="justify-between">
            <p>Whether RCM Payable: No</p>
          </article>
        )}
        <ConsumerDetails customerDetails={customerDetails} />
      </>
      )}
      <br />
      <p>{RL_NumberItemSold}: {purchasedItems.reduce((acc, item) => acc + (item.voidFlag ? 0 : item.Quantity?.Quantity), 0)}</p>
      <p>{RL_NumberItemReturned}: {returnedItems.reduce((acc, item) => acc + (item.VoidFlag ? 0 : item.Return?.Quantity?.Quantity), 0)}</p>
      {savingAmount && <p>{RL_YouSaved.replace('{amount}', `${displayAmount(savingAmount)}`)}</p>}
      <br />
    </Fragment>
  );
};

TransactionTotals.defaultProps = {
  contactDetails: [],
  country: '',
  customerDetails: [],
  purchasedItems: [],
  returnedItems: [],
  taxes: [],
  totals: []
};

TransactionTotals.propTypes = {
  contactDetails: PropTypes.arrayOf(PropTypes.shape()),
  country: PropTypes.string,
  customerDetails: PropTypes.arrayOf(PropTypes.shape()),
  purchasedItems: PropTypes.arrayOf(PropTypes.shape()),
  returnedItems: PropTypes.arrayOf(PropTypes.shape()),
  taxes: PropTypes.arrayOf(PropTypes.shape()),
  totals: PropTypes.arrayOf(PropTypes.shape())
};

export default TransactionTotals;
